import type { UserRole, WorkflowCapabilityId } from '@statura/types';
import type { ClientFundingCohort, ClientFundingSourceProgram } from '@statura/utils';
import {
  CLIENT_RECORD_TAB_IDS,
  type ClientRecordTabId,
} from '@/lib/clients/resolve-initial-client-tab';

/** Established (care_today_v1 OFF) work-area order. */
export const CLIENT_WORK_AREA_IDS = [
  'summary',
  'plan',
  'clinical',
  'delivery',
  'funding',
  'people',
  'records',
  'safety',
] as const;

export type ClientWorkAreaId = (typeof CLIENT_WORK_AREA_IDS)[number];

export type ClientWorkAreaDefinition = {
  id: ClientWorkAreaId;
  label: string;
  purpose: string;
  primaryTab: ClientRecordTabId;
  legacyTabs: readonly ClientRecordTabId[];
  modulePaths: readonly string[];
  capabilities: readonly WorkflowCapabilityId[];
};

/** Established (care_today_v1 OFF) work areas, labels and order. */
export const CLIENT_WORK_AREAS = [
  {
    id: 'summary',
    label: 'Home',
    purpose: 'Single optimised landing — what needs attention now (D1).',
    primaryTab: 'overview',
    legacyTabs: ['overview', 'sah-hub', 'rac-hub'],
    modulePaths: ['/clients', '/clients/*/sah', '/residential'],
    capabilities: ['client.care.workspace'],
  },
  {
    id: 'plan',
    label: 'Plan',
    purpose: 'Care plan projection and assessments (D9).',
    primaryTab: 'care-plans',
    legacyTabs: ['care-plans', 'assessments'],
    modulePaths: ['/care-plans', '/assessments'],
    capabilities: ['client.care.workspace'],
  },
  {
    id: 'clinical',
    label: 'Clinical',
    purpose: 'Clinical overview, medications, devices, case notes (D9).',
    primaryTab: 'clinical',
    legacyTabs: ['clinical', 'case-notes', 'medications', 'devices'],
    modulePaths: [
      '/clinical',
      '/clients/*/case-notes',
      '/medications',
      '/monitoring',
      '/observations',
    ],
    capabilities: [
      'client.care.workspace',
      'frontline.observation.record',
      'frontline.medication.administer',
      'frontline.wound.record',
    ],
  },
  {
    id: 'delivery',
    label: 'Delivery',
    purpose:
      'Coordinate scheduled, delivered and follow-up care, including a read-only visit pack of what the worker sees at the door.',
    primaryTab: 'services',
    legacyTabs: ['services', 'cm-time'],
    modulePaths: ['/home-care', '/roster'],
    capabilities: ['client.care.workspace', 'service.delivery.manage', 'roster.manage'],
  },
  {
    id: 'funding',
    label: 'Funding',
    purpose: 'Review approved, agreed, charged and outstanding funding information.',
    primaryTab: 'budget',
    legacyTabs: ['budget', 'authorisations'],
    modulePaths: ['/home-care', '/finance', '/residential'],
    capabilities: [
      'client.care.workspace',
      'sah.claim.prepare',
      'sah.statement.issue',
      'sah.athm.manage',
      'prudential.access',
    ],
  },
  {
    id: 'people',
    label: 'People',
    purpose:
      'Understand who this person is, how they want to be treated, and who is involved, accountable and authorised.',
    primaryTab: 'family',
    legacyTabs: ['family'],
    modulePaths: ['/clients', '/settings/portal'],
    capabilities: ['client.care.workspace', 'client.edit'],
  },
  {
    id: 'records',
    label: 'Records',
    purpose: 'Find evidence that has been created, shared, signed or changed.',
    primaryTab: 'documents',
    legacyTabs: ['documents', 'consent', 'history'],
    modulePaths: ['/clients', '/care-plans'],
    capabilities: ['client.care.workspace'],
  },
  {
    id: 'safety',
    label: 'Safety',
    purpose: 'Review risks, incidents and protective actions requiring attention.',
    primaryTab: 'incidents',
    legacyTabs: ['incidents'],
    modulePaths: ['/clients', '/clinical', '/compliance/incidents'],
    capabilities: ['incident.create', 'incident.review', 'complaint.create'],
  },
] as const satisfies readonly ClientWorkAreaDefinition[];

const CARE_TODAY_WORK_AREA_LABELS: Partial<Record<ClientWorkAreaId, string>> = {
  summary: 'Care Today',
  plan: 'Care Plan',
  delivery: 'Daily Care',
  people: 'About & Contacts',
  funding: 'Programme & funding',
};

/**
 * care_today_v1 ON: the residential and Support at Home programme hubs are
 * admission, funding and journey administration, so they sit in the secondary
 * Programme & funding area. Care Today then only ever selects Care Today.
 */
const CARE_TODAY_PROGRAMME_HUB_TABS = [
  'sah-hub',
  'rac-hub',
] as const satisfies readonly ClientRecordTabId[];

function careTodayLegacyTabs(area: ClientWorkAreaDefinition): readonly ClientRecordTabId[] {
  if (area.id === 'summary')
    return area.legacyTabs.filter(
      (tab) => !(CARE_TODAY_PROGRAMME_HUB_TABS as readonly ClientRecordTabId[]).includes(tab),
    );
  if (area.id === 'funding') return [...area.legacyTabs, ...CARE_TODAY_PROGRAMME_HUB_TABS];
  return area.legacyTabs;
}

/** care_today_v1 ON: Care Today labels, with Funding after People and Records. */
export const CARE_TODAY_CLIENT_WORK_AREA_IDS = [
  'summary',
  'plan',
  'clinical',
  'delivery',
  'people',
  'records',
  'funding',
  'safety',
] as const satisfies readonly ClientWorkAreaId[];

export const CARE_TODAY_CLIENT_WORK_AREAS: readonly ClientWorkAreaDefinition[] =
  CARE_TODAY_CLIENT_WORK_AREA_IDS.map((id) => {
    const area = CLIENT_WORK_AREAS.find((candidate) => candidate.id === id)!;
    return {
      ...area,
      label: CARE_TODAY_WORK_AREA_LABELS[id] ?? area.label,
      legacyTabs: careTodayLegacyTabs(area),
    };
  });

/** Work-area registry for the care_today_v1 state; OFF is the established registry. */
export function clientWorkAreas(careToday: boolean): readonly ClientWorkAreaDefinition[] {
  return careToday ? CARE_TODAY_CLIENT_WORK_AREAS : CLIENT_WORK_AREAS;
}

export const CLIENT_WORK_AREA_BY_TAB = Object.fromEntries(
  CLIENT_WORK_AREAS.flatMap((area) => area.legacyTabs.map((tab) => [tab, area.id] as const)),
) as Record<ClientRecordTabId, ClientWorkAreaId>;

const CARE_TODAY_CLIENT_WORK_AREA_BY_TAB = Object.fromEntries(
  CARE_TODAY_CLIENT_WORK_AREAS.flatMap((area) =>
    area.legacyTabs.map((tab) => [tab, area.id] as const),
  ),
) as Record<ClientRecordTabId, ClientWorkAreaId>;

/** OFF (the default) is the established mapping; ON moves the programme hubs. */
export function clientWorkAreaForTab(tab: ClientRecordTabId, careToday = false): ClientWorkAreaId {
  return (careToday ? CARE_TODAY_CLIENT_WORK_AREA_BY_TAB : CLIENT_WORK_AREA_BY_TAB)[tab];
}

export function clientWorkAreaPrimaryTab(areaId: ClientWorkAreaId): ClientRecordTabId {
  const area = CLIENT_WORK_AREAS.find((candidate) => candidate.id === areaId);
  if (!area) throw new Error(`Unknown client work area: ${areaId}`);
  return area.primaryTab;
}

/**
 * Work-area selection is stable even when layout preferences reorder retained
 * views. If the canonical landing is unavailable because of module or cohort
 * policy, use the area's first available retained view rather than stranding
 * an otherwise reachable destination.
 */
export function resolveClientWorkAreaLandingTab(
  areaId: ClientWorkAreaId,
  availableTabs: readonly ClientRecordTabId[],
  careToday = false,
): ClientRecordTabId | null {
  const area = clientWorkAreas(careToday).find((candidate) => candidate.id === areaId);
  if (!area) return null;
  if (availableTabs.includes(area.primaryTab)) return area.primaryTab;
  return area.legacyTabs.find((tab) => availableTabs.includes(tab)) ?? null;
}

/** Name the accessible destination rather than a module the actor cannot open. */
export function clientWorkAreaNavigationLabel(
  areaId: ClientWorkAreaId,
  availableTabs: readonly ClientRecordTabId[],
  careToday = false,
): string {
  const area = clientWorkAreas(careToday).find((candidate) => candidate.id === areaId);
  if (
    careToday &&
    areaId === 'plan' &&
    resolveClientWorkAreaLandingTab(areaId, availableTabs, true) === 'assessments'
  )
    return 'Assessments';
  return area?.label ?? areaId;
}

export function isClientWorkAreaId(value: unknown): value is ClientWorkAreaId {
  return (CLIENT_WORK_AREA_IDS as readonly unknown[]).includes(value);
}

/** Read owners that intentionally span more than one Phase 3 work area. */
export function clientWorkstationNeedsCmTaskEvidence(tab: ClientRecordTabId): boolean {
  return clientWorkAreaForTab(tab) === 'funding' || tab === 'cm-time';
}

export function clientWorkstationNeedsIncidentEvidence(tab: ClientRecordTabId): boolean {
  return tab === 'overview' || tab === 'incidents' || tab === 'history';
}

export type ClientWorkstationBaseProgramScenario =
  | 'SAH_ONGOING'
  | 'SAH_INTERIM_MSO'
  | 'SAH_TRANSITIONED_HCP'
  | 'SAH_RESTORATIVE'
  | 'SAH_END_OF_LIFE'
  | 'SAH_CONTEXT_REVIEW'
  | 'HCP_ONGOING'
  | 'CHSP_CURRENT'
  | 'STRC_PRE_2025'
  | 'RAC_PERMANENT'
  | 'RAC_RESPITE'
  | 'NDIS'
  | 'DVA'
  | 'TAC'
  | 'PRIVATE'
  | 'UNRESOLVED';

export type ClientWorkstationProgramFacet = 'RAC_PRE_2025_ARRANGEMENT';
export type ClientWorkstationProgramOverlay = 'SAH_AT_HM';

export type ClientWorkstationProgramResolution = {
  baseScenarios: readonly ClientWorkstationBaseProgramScenario[];
  facets: readonly ClientWorkstationProgramFacet[];
  overlays: readonly ClientWorkstationProgramOverlay[];
  mixed: boolean;
  requiresReview: boolean;
  reasons: readonly string[];
};

export type ClientWorkstationLifecycle =
  | 'WAITLIST'
  | 'ASSESSMENT'
  | 'ACTIVE'
  | 'ON_HOLD'
  | 'HOSPITAL'
  | 'RESPITE'
  | 'DISCHARGED'
  | 'DECEASED'
  | 'ANONYMISED'
  | 'ARCHIVED';

export type ClientWorkstationAccessInput = {
  programApplicable: boolean;
  lifecycleApplicable: boolean;
  moduleEntitled: boolean;
  roleCapable: boolean;
  clientScoped: boolean;
  actorActive: boolean;
  mutationQualified?: boolean;
};

export type ClientWorkstationAccessDecision = {
  readable: boolean;
  mutable: boolean;
  reason:
    | 'allowed'
    | 'not_applicable'
    | 'not_entitled'
    | 'not_permitted'
    | 'outside_client_scope'
    | 'inactive_actor'
    | 'mutation_qualification_required';
};

export function resolveClientWorkstationAccess(
  input: ClientWorkstationAccessInput,
): ClientWorkstationAccessDecision {
  if (!input.programApplicable || !input.lifecycleApplicable) {
    return { readable: false, mutable: false, reason: 'not_applicable' };
  }
  if (!input.actorActive) {
    return { readable: false, mutable: false, reason: 'inactive_actor' };
  }
  if (!input.clientScoped) {
    return { readable: false, mutable: false, reason: 'outside_client_scope' };
  }
  if (!input.moduleEntitled) {
    return { readable: false, mutable: false, reason: 'not_entitled' };
  }
  if (!input.roleCapable) {
    return { readable: false, mutable: false, reason: 'not_permitted' };
  }
  if (input.mutationQualified === false) {
    return { readable: true, mutable: false, reason: 'mutation_qualification_required' };
  }
  return { readable: true, mutable: true, reason: 'allowed' };
}

export type ResolveClientWorkstationProgramsInput = {
  fundingPrograms: readonly string[] | null | undefined;
  fundingCohort?: ClientFundingCohort | null;
  sourceProgram?: ClientFundingSourceProgram | null;
  residentialBoundary?: boolean;
  pre2025ResidentialArrangement?: boolean;
  hasAtHmRecords?: boolean;
};

const SAH_TRANSITION_COHORTS: ReadonlySet<ClientFundingCohort> = new Set([
  'HCP_TRANSITION_NO_WORSE_OFF',
  'HCP_TRANSITION_STANDARD_CONTRIBUTIONS',
]);

function normalizedFundingPrograms(
  values: readonly string[] | null | undefined,
): ReadonlySet<string> {
  return new Set((values ?? []).map((value) => String(value).trim().toUpperCase()).filter(Boolean));
}

export function resolveClientWorkstationPrograms(
  input: ResolveClientWorkstationProgramsInput,
): ClientWorkstationProgramResolution {
  const programs = normalizedFundingPrograms(input.fundingPrograms);
  const cohort = input.fundingCohort ?? null;
  const sourceProgram = input.sourceProgram ?? null;
  const base = new Set<ClientWorkstationBaseProgramScenario>();
  const facets = new Set<ClientWorkstationProgramFacet>();
  const overlays = new Set<ClientWorkstationProgramOverlay>();
  const reasons: string[] = [];
  let requiresReview = false;

  const hasSah = programs.has('SUPPORT_AT_HOME');
  const hasHcp = programs.has('HCP');

  if (hasSah || (cohort && SAH_TRANSITION_COHORTS.has(cohort))) {
    switch (cohort) {
      case 'SAH_STANDARD':
        base.add('SAH_ONGOING');
        break;
      case 'SAH_INTERIM_MSO':
        base.add('SAH_INTERIM_MSO');
        break;
      case 'HCP_TRANSITION_NO_WORSE_OFF':
      case 'HCP_TRANSITION_STANDARD_CONTRIBUTIONS':
        if (hasSah || hasHcp) {
          base.add('SAH_TRANSITIONED_HCP');
        } else {
          requiresReview = true;
          reasons.push('HCP transition cohort has no HCP or Support at Home funding token.');
        }
        break;
      case 'SAH_RESTORATIVE_PATHWAY':
        base.add('SAH_RESTORATIVE');
        break;
      case 'SAH_END_OF_LIFE_PATHWAY':
        base.add('SAH_END_OF_LIFE');
        break;
      case null:
      case 'UNKNOWN':
        if (hasSah) {
          base.add('SAH_CONTEXT_REVIEW');
          requiresReview = true;
          reasons.push('Support at Home funding has no resolved funding cohort.');
        }
        break;
      default:
        if (hasSah) {
          requiresReview = true;
          reasons.push(`Support at Home token conflicts with funding cohort ${cohort}.`);
        }
        break;
    }
  }

  if (hasHcp && !hasSah && (cohort === null || cohort === 'NON_SAH_FUNDED')) {
    base.add('HCP_ONGOING');
  }

  if (programs.has('CHSP')) {
    base.add('CHSP_CURRENT');
    if (cohort === 'CHSP_PRE_2027') {
      // Positive CHSP context: no diagnostic required.
    } else {
      requiresReview = true;
      reasons.push('CHSP funding is retained but has no CHSP_PRE_2027 context.');
    }
  }

  if (sourceProgram === 'STRC' || cohort === 'STRC_PRE_2025') {
    if (sourceProgram === 'STRC' && cohort === 'STRC_PRE_2025') {
      base.add('STRC_PRE_2025');
    } else {
      requiresReview = true;
      reasons.push('STRC source program and STRC_PRE_2025 cohort are inconsistent.');
    }
  }

  if (programs.has('AN_ACC') || programs.has('RAC_RESPITE')) {
    if (!input.residentialBoundary) {
      requiresReview = true;
      reasons.push('Residential funding token has no residential boundary.');
    } else if (programs.has('RAC_RESPITE')) {
      base.add('RAC_RESPITE');
    } else {
      base.add('RAC_PERMANENT');
    }
    if (input.residentialBoundary && input.pre2025ResidentialArrangement) {
      facets.add('RAC_PRE_2025_ARRANGEMENT');
    }
  }

  for (const program of ['NDIS', 'DVA', 'TAC', 'PRIVATE'] as const) {
    if (programs.has(program)) base.add(program);
  }

  if (input.hasAtHmRecords) {
    const hasSahBase = [...base].some((scenario) => scenario.startsWith('SAH_'));
    if (hasSahBase) {
      overlays.add('SAH_AT_HM');
    } else {
      requiresReview = true;
      reasons.push('AT-HM records exist without a positive Support at Home context.');
    }
  }

  if (base.size === 0) {
    base.add('UNRESOLVED');
    requiresReview = true;
    if (reasons.length === 0)
      reasons.push('No supported client funding context could be resolved.');
  } else if (requiresReview && !base.has('SAH_CONTEXT_REVIEW')) {
    base.add('UNRESOLVED');
  }

  const baseScenarios = [...base];
  const activeBaseCount = baseScenarios.filter((scenario) => scenario !== 'UNRESOLVED').length;

  return {
    baseScenarios,
    facets: [...facets],
    overlays: [...overlays],
    mixed: activeBaseCount > 1,
    requiresReview,
    reasons,
  };
}

export type ClientFieldAuthority = {
  id: string;
  fact: string;
  readAuthority: readonly string[];
  mutationOwners: readonly string[];
  conflictRule: string;
};

export const CLIENT_FIELD_AUTHORITIES = [
  {
    id: 'core-identity',
    fact: 'Core identity, status, address and identifiers',
    readAuthority: ['clients'],
    mutationOwners: [
      'register_client_with_funding_context_controlled',
      'update_client_controlled',
      'specialised lifecycle controlled RPCs',
    ],
    conflictRule: 'The canonical clients row wins for core identity.',
  },
  {
    id: 'contact-relationship',
    fact: 'Contact relationship and portal identity',
    readAuthority: ['client_contacts'],
    mutationOwners: [
      'record_client_contact_controlled',
      'update_client_contact_controlled',
      'archive_client_contact_controlled',
    ],
    conflictRule:
      'The active contact row wins; representative summary fields are compatibility only.',
  },
  {
    id: 'decision-authority',
    fact: 'Decision supporter, scope and authority',
    readAuthority: ['supported_decision_makers'],
    mutationOwners: [
      'upsert_supported_decision_maker_controlled',
      'set_supported_decision_maker_contact_controlled',
      'archive_supported_decision_maker_controlled',
    ],
    conflictRule: 'Never infer decision authority from clients.representative_* fields.',
  },
  {
    id: 'care-team',
    fact: 'Accountable and participating care team',
    readAuthority: ['client_care_team'],
    mutationOwners: [
      'assign_client_care_team_member_controlled',
      'reassign_client_care_team_member_controlled',
      'set_primary_care_team_member_controlled',
      'end_client_care_team_member_controlled',
    ],
    conflictRule: 'The active effective-dated assignment wins.',
  },
  {
    id: 'funding-tokens',
    fact: 'Canonical client funding tokens',
    readAuthority: ['clients.funding_programs'],
    mutationOwners: [
      'register_client_with_funding_context_controlled',
      'append_private_funding_program_controlled',
      'finalise_residential_admission_controlled',
      'finalise_residential_respite_admission_controlled',
      'convert_residential_respite_to_permanent_controlled',
    ],
    conflictRule: 'Canonical allowlisted tokens remain compatibility authority.',
  },
  {
    id: 'funding-context',
    fact: 'Funding cohort and transition context',
    readAuthority: ['client_funding_contexts'],
    mutationOwners: [
      'register_client_with_funding_context_controlled',
      'record_client_funding_context_controlled',
      'existing classification and transition controlled RPCs',
    ],
    conflictRule:
      'The current explicit cohort and provenance projection wins over default inference.',
  },
  {
    id: 'program-enrolment',
    fact: 'Program enrolment reconciliation',
    readAuthority: ['program_enrollments'],
    mutationOwners: [],
    conflictRule:
      'Optional read-only reconciliation evidence only; absence never invalidates a funding scenario.',
  },
  {
    id: 'care-plan',
    fact: 'Care plan, version and output state',
    readAuthority: ['care_plans', 'care_plan_versions', 'care_plan_outputs'],
    mutationOwners: ['certified care-plan composer controlled actions and RPCs'],
    conflictRule: 'The certified composer contract remains authoritative.',
  },
  {
    id: 'assessment',
    fact: 'Assessment and recommendation state',
    readAuthority: ['assessments', 'assessment_recommendations'],
    mutationOwners: ['existing assessment and recommendation controlled actions'],
    conflictRule: 'The workstation does not recompute clinical recommendation state.',
  },
  {
    id: 'delivery',
    fact: 'Scheduled and delivered service evidence',
    readAuthority: ['roster_shifts', 'services_delivered'],
    mutationOwners: ['existing scheduling, delivery and correction controlled actions'],
    conflictRule: 'The dated domain record wins; correction uses the existing correction path.',
  },
  {
    id: 'visit-pack',
    fact: 'Worker door-step projection (read)',
    readAuthority: ['clients', 'client_allergies', 'home_assessments', 'resident_diet_profiles'],
    mutationOwners: [],
    conflictRule: 'Read-only projection of existing editors; never a second writer.',
  },
  {
    id: 'home-access',
    fact: 'Home access, parking, pets and door code',
    readAuthority: ['home_assessments'],
    mutationOwners: ['record_home_assessment_controlled'],
    conflictRule:
      'Latest COMPLETED FULL_HOME assessment wins; the client row is never a second access editor.',
  },
  {
    id: 'communication-preferences',
    fact: 'How we care: communication, languages, interpreter, carer gender, culture, religion, country of birth and life story',
    readAuthority: ['clients'],
    mutationOwners: ['update_client_controlled'],
    conflictRule:
      'clients.gender_preference is shown on the worker profile; roster matching uses shift_requirements.gender_preference.',
  },
  {
    id: 'diet-profile',
    fact: 'Diet (RAC profile authoritative; SAH free-text fallback)',
    readAuthority: ['resident_diet_profiles', 'clients.dietary_requirements'],
    mutationOwners: ['upsert_resident_diet_profile_controlled', 'update_client_controlled'],
    conflictRule:
      'RAC resident_diet_profiles wins when present; SAH dietary_requirements is the fallback only.',
  },
  {
    id: 'finance',
    fact: 'Budget, contribution, fees, statements and invoices',
    readAuthority: ['existing program-specific finance ledgers'],
    mutationOwners: ['existing program-specific controlled finance actions and RPCs'],
    conflictRule: 'The effective-dated program ledger wins.',
  },
  {
    id: 'evidence',
    fact: 'Documents, signatures, consent and history',
    readAuthority: ['documents', 'signature_blocks', 'consents', 'timeline projections'],
    mutationOwners: ['existing controlled upload, sign, consent, revoke and supersede actions'],
    conflictRule: 'Immutable evidence is retained; corrections supersede rather than overwrite.',
  },
] as const satisfies readonly ClientFieldAuthority[];

export type ClientMutationPolicy = {
  id: string;
  owners: readonly string[];
  roles: readonly UserRole[];
};

export const CLIENT_MUTATION_POLICIES = [
  {
    id: 'registration',
    owners: ['register_client_with_funding_context_controlled'],
    roles: ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'COORDINATOR', 'CASE_MANAGER'],
  },
  {
    id: 'client-update',
    owners: ['update_client_controlled'],
    roles: [
      'SYSTEM_ADMIN',
      'TENANT_ADMIN',
      'OWNER',
      'COORDINATOR',
      'CASE_MANAGER',
      'FACILITY_MANAGER',
      'REGION_MANAGER',
      'CLINICAL_LEAD',
      'REGISTERED_NURSE',
    ],
  },
  {
    id: 'funding-context',
    owners: ['record_client_funding_context_controlled'],
    roles: [
      'SYSTEM_ADMIN',
      'TENANT_ADMIN',
      'OWNER',
      'COORDINATOR',
      'CASE_MANAGER',
      'FACILITY_MANAGER',
      'REGION_MANAGER',
      'CLINICAL_LEAD',
      'FINANCE_OFFICER',
    ],
  },
  {
    id: 'contacts',
    owners: [
      'record_client_contact_controlled',
      'update_client_contact_controlled',
      'archive_client_contact_controlled',
    ],
    roles: ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'COORDINATOR'],
  },
  {
    id: 'decision-authority',
    owners: [
      'upsert_supported_decision_maker_controlled',
      'set_supported_decision_maker_contact_controlled',
      'archive_supported_decision_maker_controlled',
    ],
    roles: ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'COORDINATOR', 'CLINICAL_LEAD'],
  },
  {
    id: 'care-team',
    owners: [
      'assign_client_care_team_member_controlled',
      'reassign_client_care_team_member_controlled',
      'set_primary_care_team_member_controlled',
      'end_client_care_team_member_controlled',
    ],
    roles: [
      'SYSTEM_ADMIN',
      'TENANT_ADMIN',
      'FACILITY_MANAGER',
      'COORDINATOR',
      'CASE_MANAGER',
      'CLINICAL_LEAD',
    ],
  },
  {
    id: 'private-funding-append',
    owners: ['append_private_funding_program_controlled'],
    roles: [
      'SYSTEM_ADMIN',
      'TENANT_ADMIN',
      'FACILITY_MANAGER',
      'FINANCE_OFFICER',
      'COORDINATOR',
      'CLINICAL_LEAD',
    ],
  },
  {
    id: 'sah-client-exit',
    owners: ['exit_sah_client_controlled'],
    roles: ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'COORDINATOR'],
  },
  {
    id: 'community-client-transition',
    owners: ['complete_community_client_transition_controlled'],
    roles: [
      'SYSTEM_ADMIN',
      'TENANT_ADMIN',
      'OWNER',
      'FACILITY_MANAGER',
      'REGION_MANAGER',
      'COORDINATOR',
      'CASE_MANAGER',
      'CLINICAL_LEAD',
      'REGISTERED_NURSE',
    ],
  },
  {
    id: 'residential-client-transition',
    owners: ['complete_residential_transition_controlled'],
    roles: [
      'SYSTEM_ADMIN',
      'TENANT_ADMIN',
      'FACILITY_MANAGER',
      'COORDINATOR',
      'REGISTERED_NURSE',
      'CLINICAL_LEAD',
    ],
  },
] as const satisfies readonly ClientMutationPolicy[];

export type ClientActionOwner = {
  factKey: string;
  sourceRegion: string;
  completionOwner: 'action-queue' | 'work-area';
  workArea: ClientWorkAreaId;
  targetTab: ClientRecordTabId;
  mutationPolicy: (typeof CLIENT_MUTATION_POLICIES)[number]['id'] | null;
};

export const CLIENT_ACTION_OWNERS = [
  {
    factKey: 'assessment.recommendation-follow-up',
    sourceRegion: 'Assessment/clinical action source',
    completionOwner: 'action-queue',
    workArea: 'plan',
    targetTab: 'assessments',
    mutationPolicy: null,
  },
  {
    factKey: 'care-plan.next-required-step',
    sourceRegion: 'Zone B',
    completionOwner: 'action-queue',
    workArea: 'plan',
    targetTab: 'care-plans',
    mutationPolicy: null,
  },
  {
    factKey: 'delivery.follow-up',
    sourceRegion: 'Zone B',
    completionOwner: 'action-queue',
    workArea: 'delivery',
    targetTab: 'services',
    mutationPolicy: null,
  },
  {
    factKey: 'funding.context-review',
    sourceRegion: 'Journey readiness',
    completionOwner: 'action-queue',
    workArea: 'funding',
    targetTab: 'budget',
    mutationPolicy: 'funding-context',
  },
  {
    factKey: 'people.decision-authority-review',
    sourceRegion: 'Zone B',
    completionOwner: 'work-area',
    workArea: 'people',
    targetTab: 'family',
    mutationPolicy: 'decision-authority',
  },
  {
    factKey: 'safety.critical-follow-up',
    sourceRegion: 'Zone D',
    completionOwner: 'action-queue',
    workArea: 'safety',
    targetTab: 'incidents',
    mutationPolicy: null,
  },
] as const satisfies readonly ClientActionOwner[];

export type ClientActionRegionDisposition = {
  id: string;
  currentRegion: string;
  canonicalOwner: 'action-queue' | 'notices' | 'summary-progress' | 'work-area' | 'navigation';
  postState: string;
  removeAfterParity?: boolean;
};

export const CLIENT_ACTION_REGION_DISPOSITIONS = [
  {
    id: 'zone-b',
    currentRegion: 'Zone B / ClientRecordZoneBStrip',
    canonicalOwner: 'action-queue',
    postState: 'Sole ranked current/later action owner.',
  },
  {
    id: 'zone-d',
    currentRegion: 'Zone D / ClientRecordZoneDNotices',
    canonicalOwner: 'notices',
    postState: 'Safety and clinical warnings only; advisory notices collapse.',
  },
  {
    id: 'client-next-actions',
    currentRegion: 'Assessment/clinical action source',
    canonicalOwner: 'action-queue',
    postState:
      'Assessment, recommendation and clinical-task sources merge into the queue; the parity-proven panel is retired.',
  },
  {
    id: 'journey-stages',
    currentRegion: 'SAH and RAC journey stages',
    canonicalOwner: 'summary-progress',
    postState: 'Show progress only; actions link to the queue owner.',
  },
  {
    id: 'government-readiness',
    currentRegion: 'Services Australia and program readiness blocks',
    canonicalOwner: 'summary-progress',
    postState: 'Non-blocking diagnostics collapse; blockers contribute one queue item.',
  },
  {
    id: 'domain-panels',
    currentRegion: 'Domain cards and panels',
    canonicalOwner: 'work-area',
    postState:
      'Retain record-specific state and local controls without duplicating the next action.',
  },
  {
    id: 'related-links',
    currentRegion: 'Related links collections',
    canonicalOwner: 'navigation',
    postState: 'Use work-area navigation or an exact destination link only.',
  },
] as const satisfies readonly ClientActionRegionDisposition[];

export const CLIENT_ACTION_PRIORITY = [
  'immediate-safety-or-statutory',
  'today-blocker',
  'due-today',
  'scheduled-upcoming',
  'later-administration',
] as const;

export type ClientPlanClinicalDefault = {
  roles: readonly UserRole[];
  defaultTab: Extract<
    ClientRecordTabId,
    'care-plans' | 'clinical' | 'assessments' | 'medications' | 'devices'
  > | null;
};

export const CLIENT_PLAN_CLINICAL_DEFAULTS = [
  { roles: ['COORDINATOR', 'CASE_MANAGER'], defaultTab: 'care-plans' },
  {
    roles: ['REGISTERED_NURSE', 'ENROLLED_NURSE', 'CLINICAL_LEAD', 'TEAM_LEADER'],
    defaultTab: 'clinical',
  },
  { roles: ['ALLIED_HEALTH', 'CARE_WORKER', 'AGENCY_CONTRACTOR'], defaultTab: 'clinical' },
  { roles: ['QUALITY_OFFICER', 'COMPLIANCE_OFFICER'], defaultTab: 'assessments' },
  { roles: ['FINANCE_OFFICER', 'PAYROLL_OFFICER'], defaultTab: null },
  {
    roles: ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'FACILITY_MANAGER', 'REGION_MANAGER'],
    defaultTab: 'care-plans',
  },
] as const satisfies readonly ClientPlanClinicalDefault[];

export function assertCompleteClientWorkAreaContract(): void {
  const tabs = CLIENT_WORK_AREAS.flatMap((area) => [...area.legacyTabs]);
  if (tabs.length !== CLIENT_RECORD_TAB_IDS.length || new Set(tabs).size !== tabs.length) {
    throw new Error('Client work-area registry must map each client tab exactly once.');
  }
  for (const tab of CLIENT_RECORD_TAB_IDS) {
    if (!tabs.includes(tab)) throw new Error(`Client work-area registry is missing tab ${tab}.`);
  }
}

/** Financial setup belongs to Budget/Residential in both record presentations. */
export function showClientContributionSummary(
  tab: ClientRecordTabId,
  // Retain the caller contract while placement is shared by both presentations.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _careToday: boolean,
): boolean {
  return tab === 'budget' || tab === 'rac-hub';
}
